import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, } from './helpers';
import { ColumnDataTypes, createTableQuerySchema, FilterOperators, FiltersSchema, FilterValueSchema, TableResponseMetaSchema, } from './table';
import { Severity, severitySchema } from './shared/severity';
import { filterOptionItemSchema } from './mcp';
import { ENDPOINT_FILTER_FIELD, ENDPOINT_FILTER_OPERATORS, endpointRowSummaryShape, } from './endpoints';
import { loggedInUserCountsSchema } from './shadow-ai-logged-in-user';
import { knownTotalQuerySchema } from './known-total';
import { toolApprovalQueryParamSchema, toolApprovalSchema, } from './shadow-ai-tool-approvals';
// ============================================================================
// Filterable Fields
// ============================================================================
export const INSTALLED_TOOLS_FILTERABLE_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    RISK: 'risk',
    USERS: 'users',
    FIRST_DETECTED: 'firstDetected',
    LAST_SEEN: 'lastSeen',
    // KIR-2916: filter tools by their users' logged-in account email.
    LOGGED_IN_USER: 'loggedInUser',
    // KIR-5098: tools installed on an endpoint matching this name or ID.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
// ============================================================================
// Sortable Fields
// ============================================================================
export const INSTALLED_TOOLS_SORTABLE_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    RISK: 'risk',
    USERS: 'users',
    FIRST_DETECTED: 'firstDetected',
    LAST_SEEN: 'lastSeen',
    // KIR-3429: lexicographic sort on the (personal, corporate, matched) count
    // tuple. Computed in the handler (not SQL), see list-installed-tools.ts.
    LOGGED_IN_USER: 'loggedInUser',
    // KIR-5098: number of distinct endpoints.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
export const installedToolsSortFieldSchema = z.enum([
    INSTALLED_TOOLS_SORTABLE_FIELDS.NAME,
    INSTALLED_TOOLS_SORTABLE_FIELDS.CATEGORY,
    INSTALLED_TOOLS_SORTABLE_FIELDS.RISK,
    INSTALLED_TOOLS_SORTABLE_FIELDS.USERS,
    INSTALLED_TOOLS_SORTABLE_FIELDS.FIRST_DETECTED,
    INSTALLED_TOOLS_SORTABLE_FIELDS.LAST_SEEN,
    INSTALLED_TOOLS_SORTABLE_FIELDS.LOGGED_IN_USER,
    INSTALLED_TOOLS_SORTABLE_FIELDS.ENDPOINT,
]);
// ============================================================================
// Searchable Fields
// ============================================================================
/**
 * Fields that support free-text search.
 * Excludes date fields — dates are not searchable.
 */
export const INSTALLED_TOOLS_SEARCHABLE_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    RISK: 'risk',
    USERS: 'users',
    // KIR-5098: endpoint name or ID.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
// ============================================================================
// Filter Option Fields (dynamic endpoints only)
// ============================================================================
/**
 * Fields that have a backend /filter-options endpoint.
 * risk uses static options; firstDetected/lastSeen use date pickers.
 */
export const INSTALLED_TOOLS_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    USERS: 'users',
    // KIR-3429: distinct logged-in account values for the multiselect filter.
    LOGGED_IN_USER: 'loggedInUser',
    // KIR-5098: endpoint suggestions for the Matches list.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
export const installedToolsFilterOptionFieldSchema = z.enum([
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.NAME,
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.CATEGORY,
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.USERS,
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.LOGGED_IN_USER,
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.ENDPOINT,
]);
// ============================================================================
// Response Item
// ============================================================================
export const installedToolListItemSchema = z.object({
    toolId: z.string(),
    name: z.string(),
    category: z.string(),
    riskLevel: severitySchema.exclude([Severity.Critical]),
    userCount: z.number().int().min(0),
    firstDetected: z.string().datetime({ offset: true }).nullable(),
    lastSeen: z.string().datetime({ offset: true }).nullable(),
    // KIR-2916: present only when the logged-in-user feature is enabled.
    loggedInUserCounts: loggedInUserCountsSchema.optional(),
    // KIR-3495: per-user detection evidence, returned ONLY when the request
    // narrows to a single member (`scopeUserId`). Org-wide the row is an
    // aggregate across users, where "which sources found it" has no single
    // answer — hence optional rather than nullable.
    sources: z.array(z.string()).optional(),
    extras: z.record(z.string(), z.string()).nullable().optional(),
    // KIR-4854: org-wide approval state. null = not approved; absent when the
    // tool-approval feature flag is off.
    approval: toolApprovalSchema.nullable().optional(),
    // KIR-4024: the row's users running Claude Code in auto or YOLO mode. Only
    // on Claude Code rows with at least one, while the flag is on.
    autoModeUserCount: z.number().int().min(1).optional(),
    // KIR-5098: endpoint count + the endpoints matching an endpoint filter.
    ...endpointRowSummaryShape,
});
// ============================================================================
// Filter Column Data Types (used internally by the query schema)
// ============================================================================
export const INSTALLED_TOOLS_FILTER_COLUMN_DATA_TYPES = {
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.CATEGORY]: ColumnDataTypes.ENUM,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.RISK]: ColumnDataTypes.ENUM,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.ENUM,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED]: ColumnDataTypes.DATE,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.LAST_SEEN]: ColumnDataTypes.DATE,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.LOGGED_IN_USER]: ColumnDataTypes.STRING,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.ENDPOINT]: ColumnDataTypes.STRING,
};
// Both date columns drop `equals`/`notEquals`, keeping the range plus value-less operators.
export const INSTALLED_TOOLS_OPERATOR_OVERRIDES = {
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.ENDPOINT]: ENDPOINT_FILTER_OPERATORS,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.LAST_SEEN]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
// ============================================================================
// Filter Options Schemas
// ============================================================================
export const installedToolsFilterOptionsQuerySchema = z.object({
    field: installedToolsFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    // KIR-3495: scope the options to a single org member (Shadow AI user-scope
    // page). Honoured only for manager/owner/support — everyone else is already
    // pinned to their own rows, so this can never widen what a caller may see.
    scopeUserId: z.string().uuid().optional(),
});
export const installedToolsFilterOptionsResponseSchema = z.object({
    field: installedToolsFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const installedToolsTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: installedToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
    // KIR-3495: restrict the whole result set to one org member so the Shadow AI
    // user-scope page reuses this table with per-user aggregates (userCount,
    // lastSeen). Privileged roles only — see the table engine.
    scopeUserId: z.string().uuid().optional(),
    // KIR-4854: Show filter (not_approved | approved | all). Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
export const installedToolsTableQuerySchema = createTableQuerySchema(installedToolsTableQueryBaseSchema, INSTALLED_TOOLS_FILTER_COLUMN_DATA_TYPES, INSTALLED_TOOLS_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        if (filter.field === INSTALLED_TOOLS_FILTERABLE_FIELDS.USERS) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().uuid().safeParse(v).success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Filter values for users field must be valid UUIDs',
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
        if (filter.field === INSTALLED_TOOLS_FILTERABLE_FIELDS.RISK) {
            const validRisk = severitySchema.exclude([Severity.Critical]);
            filter.value.forEach((v, valueIndex) => {
                if (!validRisk.safeParse(v).success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for risk field must be one of: ${validRisk.options.join(', ')}`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
        if (filter.field === INSTALLED_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED ||
            filter.field === INSTALLED_TOOLS_FILTERABLE_FIELDS.LAST_SEEN) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for ${filter.field} field must be valid ISO 8601 datetime strings`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
});
// ============================================================================
// API Client Params Schema (for serializing DataTable query params)
// ============================================================================
export const listInstalledToolsDataTableParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: installedToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: z.array(FilterValueSchema).optional(),
    knownTotal: knownTotalQuerySchema,
    scopeUserId: z.string().uuid().optional(),
    // KIR-4854: Show filter (not_approved | approved | all). Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
// ============================================================================
// Table Response Schema
// ============================================================================
export const installedToolsTableResponseSchema = z.object({
    data: z.array(installedToolListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
// ============================================================================
// Static Filter Option Constants (for frontend use)
// ============================================================================
export const INSTALLED_TOOLS_RISK_OPTIONS = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
];
//# sourceMappingURL=shadow-ai-installed-tools.js.map